/**
 * Print HTML content by opening it in a new tab.
 *
 * ── Why a new tab ──────────────────────────────────────────────────────
 * Samsung tablet's print service generates its on-screen preview by
 * snapshotting the current tab's document. If we print from the
 * dashboard tab — via window.print(), an iframe, or a DOM swap — the
 * preview ends up showing dashboard chrome, open modals, toast
 * notifications, etc., across 2-3 pages.
 *
 * Opening a dedicated blank tab containing ONLY the prescription HTML
 * makes that impossible: the only thing in that tab's document is the
 * paper, so Samsung's preview can't capture anything else.
 *
 * ── Why we DON'T auto-close the tab ────────────────────────────────────
 * Previous versions listened for `afterprint` and closed the new tab
 * when the print dialog closed. On Samsung tablets `afterprint` can
 * fire in weird situations — before the preview is fully up, or when
 * the OS rejects the print intent for reasons outside the web app's
 * control (no approved printer, etc.). The user would see the tab
 * open and instantly close, with no chance to see or retry the
 * preview. We now leave the tab open; the user closes it themselves
 * when they're done, which is one extra tap but 100% reliable.
 *
 * ── Popup-blocker avoidance ────────────────────────────────────────────
 * Chrome / Samsung Internet / Firefox all require window.open() to be
 * called **inside** a user-gesture handler (a click). If printHtml is
 * ever called from an async callback that has already awaited
 * anything, the open will silently fail. Every call site in this app
 * is already synchronous on the click — do not change that.
 */

// Bumped whenever this file is touched — surfaces in DevTools so you
// can verify the tablet is actually running the latest build (not a
// stale HTTP-cached one). Check with: window.nwPrintVersion
export const NW_PRINT_VERSION = '2026-04-21-new-tab-no-autoclose-v18';
if (typeof window !== 'undefined') {
  (window as any).nwPrintVersion = NW_PRINT_VERSION;
  try {
    // eslint-disable-next-line no-console
    console.log('[noWaiting] print utils version:', NW_PRINT_VERSION);
  } catch { /* ignore */ }
}

export function openPrintWindow(): Window | null {
  const w = window.open('', '_blank');

  if (!w || w.closed || typeof w.document === 'undefined') {
    try {
      // eslint-disable-next-line no-alert
      alert('Please allow popups for this site to enable printing.');
    } catch { /* ignore */ }
    return null;
  }

  return w;
}

export function printHtml(html: string, targetWindow?: Window | null): void {
  // ── 1. Open the new tab SYNCHRONOUSLY ──────────────────────────────
  // Must happen in the same tick as the user's click; any prior await
  // in the call chain will cause browsers to treat this as a popup and
  // silently block it.
  const w = targetWindow || openPrintWindow();
  if (!w) return;

  // ── 2. Write the prescription HTML + a manual Print button ─────────
  // The button is visible on screen (hidden in @media print) so that
  // if the browser's auto-print call is silently blocked — which
  // Samsung does in some situations — the user can tap the button
  // themselves. Same user-gesture guarantee, but now in the context
  // of the already-foreground new tab.
  const normalisedHtml = injectPrintToolbarAndReset(html);
  try {
    w.document.open();
    w.document.write(normalisedHtml);
    w.document.close();
  } catch (err) {
    try { w.close(); } catch { /* ignore */ }
    try {
      // eslint-disable-next-line no-console
      console.error('[noWaiting] failed to write print document:', err);
    } catch { /* ignore */ }
    return;
  }

  try {
    if (!w.document.title) w.document.title = 'Print';
  } catch { /* ignore */ }

  // ── 3. Wire the toolbar buttons inside the new tab ─────────────────
  const wireToolbar = () => {
    try {
      const printBtn = w.document.getElementById('__nw_print_btn__');
      if (printBtn) {
        printBtn.addEventListener('click', () => {
          try { w.focus(); } catch { /* ignore */ }
          try { w.print(); } catch { /* ignore */ }
        });
      }
      const closeBtn = w.document.getElementById('__nw_close_btn__');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          try { w.close(); } catch { /* ignore */ }
        });
      }
    } catch { /* ignore */ }
  };

  // ── 4. When ready (DOM + images), auto-trigger print once ──────────
  // If this is blocked, no harm done — the toolbar button works as
  // fallback. We do NOT close the tab on afterprint.
  const triggerPrint = () => {
    wireToolbar();
    waitForImages(w)
      .catch(() => { /* ignore image errors, still try to print */ })
      .then(() => {
        setTimeout(() => {
          try { w.focus(); } catch { /* ignore */ }
          try {
            w.print();
          } catch (err) {
            try {
              // eslint-disable-next-line no-console
              console.error('[noWaiting] print() failed:', err);
            } catch { /* ignore */ }
          }
        }, 350);
      });
  };

  if (w.document.readyState === 'complete') {
    triggerPrint();
  } else {
    const onLoad = () => {
      try { w.removeEventListener('load', onLoad); } catch { /* ignore */ }
      triggerPrint();
    };
    try {
      w.addEventListener('load', onLoad);
    } catch {
      triggerPrint();
    }
  }
}

/* ─────────────────────────────────────────────────────────────────────
 * Helpers
 * ─────────────────────────────────────────────────────────────────── */

/**
 * Inject:
 *   - a <style> with print-sanity resets (and a rule that hides the
 *     toolbar in @media print)
 *   - a small toolbar with Print and Close buttons just inside <body>
 *     so the user always has a way to trigger the action manually if
 *     the browser's automatic print call gets rate-limited / blocked.
 */
function injectPrintToolbarAndReset(html: string): string {
  const reset = `<style id="__nw_print_reset__">
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
    }
    #__nw_print_toolbar__ {
      position: sticky;
      top: 0;
      left: 0;
      right: 0;
      z-index: 2147483647;
      display: flex;
      gap: 12px;
      justify-content: center;
      align-items: center;
      padding: 12px 16px;
      background: #1677ff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
    }
    #__nw_print_toolbar__ button {
      appearance: none;
      border: 0;
      border-radius: 6px;
      padding: 10px 20px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      min-height: 44px;
    }
    #__nw_print_btn__ {
      background: #ffffff;
      color: #1677ff;
    }
    #__nw_close_btn__ {
      background: transparent;
      color: #ffffff;
      border: 1px solid rgba(255,255,255,0.7) !important;
    }
    @page { size: auto; margin: 0; }
    @media print {
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: #ffffff !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      #__nw_print_toolbar__ { display: none !important; }
    }
  </style>`;

  const toolbar = `<div id="__nw_print_toolbar__" role="toolbar" aria-label="Print controls">
    <button type="button" id="__nw_print_btn__">Print</button>
    <button type="button" id="__nw_close_btn__">Close</button>
  </div>`;

  // Inject reset into <head>
  let out = html;
  if (/<\/head>/i.test(out)) {
    out = out.replace(/<\/head>/i, `${reset}</head>`);
  } else if (/<head[^>]*>/i.test(out)) {
    out = out.replace(/<head([^>]*)>/i, `<head$1>${reset}`);
  } else if (/<html[^>]*>/i.test(out)) {
    out = out.replace(/<html([^>]*)>/i, `<html$1><head>${reset}</head>`);
  } else {
    out = `<!doctype html><html><head>${reset}</head><body>${out}</body></html>`;
  }

  // Inject toolbar as the first body child
  if (/<body[^>]*>/i.test(out)) {
    out = out.replace(/<body([^>]*)>/i, `<body$1>${toolbar}`);
  } else {
    out = out.replace(/<\/head>/i, `</head><body>${toolbar}`);
  }

  return out;
}

/**
 * Resolve once every <img> in the given window has finished loading
 * (or erroring). Samsung's print spooler otherwise snapshots mid-
 * decode and the clinic logo shows up as a blank box.
 */
function waitForImages(w: Window): Promise<void> {
  let imgs: HTMLImageElement[] = [];
  try {
    imgs = Array.from(w.document.images) as HTMLImageElement[];
  } catch {
    return Promise.resolve();
  }

  if (imgs.length === 0) return Promise.resolve();

  const waits = imgs.map((img) => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        try { img.removeEventListener('load', done); } catch { /* ignore */ }
        try { img.removeEventListener('error', done); } catch { /* ignore */ }
        resolve();
      };
      try {
        img.addEventListener('load', done);
        img.addEventListener('error', done);
      } catch {
        resolve();
      }
      setTimeout(done, 5000);
    });
  });

  return Promise.all(waits).then(() => undefined);
}
